import React, { useEffect, useState } from "react";
import {
  Button,
  Space,
  Table,
  Tag,
  Input,
  Popconfirm,
  Modal,
  InputNumber,
  Form,
  Alert,
} from "antd";
import { PlusOutlined, EyeOutlined, StopOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import purchaseService from "../../services/purchaseService";
import PageHeader from "../../components/PageHeader";

const Purchases: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [detail, setDetail] = useState<any>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [payModal, setPayModal] = useState<any>(null);
  const [payForm] = Form.useForm();
  const navigate = useNavigate();

  const load = async (page = 1, q = search) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await purchaseService.getAll({
        page,
        limit: pagination.pageSize,
        search: q || undefined,
      });
      setItems(res.data.data);
      setPagination((p) => ({
        ...p,
        current: res.data.pagination.currentPage,
        total: res.data.pagination.totalItems,
      }));
    } catch (e: any) {
      setLoadError(e.response?.data?.message || "Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const viewDetail = async (id: string) => {
    setDetailError(null);
    setDetail({ loading: true });
    try {
      const res = await purchaseService.getById(id);
      setDetail(res.data.data);
    } catch {
      setDetail(null);
      setDetailError("Failed to load invoice");
    }
  };

  const handleVoid = async (id: string) => {
    try {
      await purchaseService.voidInvoice(id, "Voided from UI");
      load(pagination.current);
    } catch {
      // API errors toasted by interceptor
    }
  };

  const savePayment = async () => {
    const values = await payForm.validateFields();
    try {
      await purchaseService.updatePayment(payModal._id, values);
      setPayModal(null);
      load(pagination.current);
    } catch {
      // API errors toasted by interceptor
    }
  };

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    {
      title: "Supplier",
      dataIndex: ["supplier", "name"],
      key: "supplier",
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      render: (d: string) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "Items",
      render: (_: any, r: any) => r.items?.length || 0,
    },
    {
      title: "Total",
      dataIndex: "grandTotal",
      render: (v: number) => `₹${(v || 0).toFixed(2)}`,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      render: (v: number) => `₹${(v || 0).toFixed(2)}`,
    },
    {
      title: "Memo",
      dataIndex: "memoType",
      render: (v: string) => (
        <Tag color={v === "cash" ? "green" : "blue"}>
          {(v || "credit").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      render: (s: string) => (
        <Tag
          color={
            s === "paid" ? "green" : s === "partially_paid" ? "orange" : "red"
          }
        >
          {s}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => viewDetail(record._id)}
          />
          <Button type="link" onClick={() => {
            setPayModal(record);
            payForm.setFieldsValue({
              amountPaid: record.amountPaid,
              paymentStatus: record.paymentStatus,
            });
          }}>
            Pay
          </Button>
          <Popconfirm
            title="Void this invoice and reverse stock?"
            onConfirm={() => handleVoid(record._id)}
          >
            <Button type="link" danger icon={<StopOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Purchase Invoices"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/purchases/new")}
          >
            New Purchase
          </Button>
        }
      />
      <div className="page-panel">
        {loadError && (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={
              <Button size="small" onClick={() => load(pagination.current)}>
                Retry
              </Button>
            }
            style={{ marginBottom: 10 }}
          />
        )}
        {detailError && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setDetailError(null)}
            message={detailError}
            style={{ marginBottom: 10 }}
          />
        )}
        <Input.Search
          placeholder="Search invoice number"
          allowClear
          style={{ width: 280, marginBottom: 10 }}
          onSearch={(v) => {
            setSearch(v);
            load(1, v);
          }}
        />
        <Table
          className="dense-table"
          size="small"
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{
            ...pagination,
            onChange: (p) => load(p),
          }}
        />
      </div>

      <Modal
        title="Invoice Details"
        open={!!detail && !detail.loading}
        onCancel={() => setDetail(null)}
        footer={null}
        width={900}
      >
        {detail && !detail.loading && (
          <>
            <p>
              <strong>{detail.invoiceNumber}</strong> — {detail.supplier?.name}
            </p>
            <p>
              Date: {dayjs(detail.invoiceDate).format("DD/MM/YYYY")} | Total: ₹
              {detail.grandTotal?.toFixed(2)} | Tax: ₹
              {detail.totalTax?.toFixed(2)}
            </p>
            <Table
              className="dense-table"
              size="small"
              pagination={false}
              rowKey={(_, i) => String(i)}
              dataSource={detail.items}
              columns={[
                {
                  title: "Product",
                  render: (_: any, r: any) =>
                    r.medicine?.name || r.medicine?.code || "—",
                },
                { title: "Batch", dataIndex: "batchNumber", width: 90 },
                { title: "Qty", dataIndex: "quantity", width: 60 },
                { title: "Free", dataIndex: "freeQuantity", width: 55 },
                {
                  title: "Pack",
                  dataIndex: "packSize",
                  width: 55,
                  render: (v: number) => v || 1,
                },
                {
                  title: "Stock",
                  dataIndex: "stockQuantity",
                  width: 70,
                  render: (v: number, r: any) =>
                    v != null ? v : (r.quantity || 0) * (r.packSize || 1),
                },
                {
                  title: "Rate",
                  dataIndex: "purchaseRate",
                  width: 80,
                  render: (v: number) => `₹${Number(v || 0).toFixed(2)}`,
                },
                {
                  title: "GST%",
                  dataIndex: "gstRate",
                  width: 60,
                  render: (v: number) => v ?? 0,
                },
                {
                  title: "Line Total",
                  dataIndex: "lineTotal",
                  width: 100,
                  render: (v: number) => `₹${Number(v || 0).toFixed(2)}`,
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Update Payment"
        open={!!payModal}
        onCancel={() => setPayModal(null)}
        onOk={savePayment}
      >
        <Form form={payForm} layout="vertical">
          <Form.Item name="amountPaid" label="Amount Paid" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="paymentStatus" label="Status">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Purchases;
